/**
 * Trailblazers (TRL) galaxy-map types
 *
 * Trailblazers is played on a node graph ("galaxy map") the admin designs:
 * planets (nodes) connected by curved lines (edges). Teams pick ONE starting
 * planet and progress independently along the connections, converging on a
 * single final planet. Some nodes are flagged as checkpoints (barriers where
 * teams wait for the field to catch up).
 *
 * Round "level" is DERIVED from the graph topology (see lifecycle/trlTopology),
 * never authored directly — start nodes are level 1, their successors level 2,
 * and so on to the single final node.
 *
 * IMPORTANT: a node references library images by id only. The answer
 * coordinates on {@link TrlImageAsset} are SERVER ONLY and never travel inside
 * these DTOs — only the public metadata (`imageUrl`/`imageName`/`imageArea`).
 *
 * Prefix: "Trl"
 */
import { DEFAULT_TRL_BASE_POINTS, DEFAULT_TRL_POINTS_DECREMENT, } from './round';
/**
 * The key a screenshot's place is compared by in same-planet checks: its
 * platform area (`area:<logAreaId>`). Undefined when it has none (an unassigned
 * slot), which the checks skip.
 */
export function trlImageAreaKey(image) {
    return image?.logAreaId != null ? `area:${image.logAreaId}` : undefined;
}
/**
 * The patterns an admin may author in the map builder. `solid` is deliberately
 * absent — it carries the "your team travelled this path" meaning on the player
 * map, so letting it be authored would make the two indistinguishable.
 */
export const TRL_AUTHORABLE_EDGE_PATTERNS = ['dashed', 'dotted'];
/**
 * Effective default look for an edge with no (or partial) authored style.
 *
 * Dashed rather than solid: a solid line means "this team travelled here", so
 * the galaxy's un-travelled paths must never default into that look.
 */
export const DEFAULT_TRL_EDGE_STYLE = {
    color: 'rgba(255, 255, 255, 0.82)',
    width: 1.5,
    pattern: 'dashed',
    rounded: true,
};
/**
 * Layer an edge's own overrides over the map-wide default stroke.
 *
 * Merged FIELD BY FIELD rather than by object spread: a spread would let an
 * explicitly-`undefined` key on `style` clobber the map default. Returns
 * `undefined` when neither side authored anything, so the caller's per-surface
 * fallback in {@link edgeStyleToSvg} still applies (white in the admin builder,
 * cyan on the player map) for maps saved before default strokes existed.
 */
export function resolveTrlEdgeStyle(style, mapDefault) {
    if (!style)
        return mapDefault;
    if (!mapDefault)
        return style;
    return {
        color: style.color ?? mapDefault.color,
        width: style.width ?? mapDefault.width,
        pattern: style.pattern ?? mapDefault.pattern,
        rounded: style.rounded ?? mapDefault.rounded,
    };
}
/**
 * Resolve a (possibly partial/absent) {@link TrlEdgeStyle} into concrete SVG
 * stroke props. Dash/dot lengths are derived from the resolved stroke width so
 * the pattern reads correctly at any thickness. Callers pass their surface's
 * native default color/width so an *unstyled* edge keeps its original look
 * (white in the admin builder, cyan on the player map) while an *authored*
 * style renders identically on both.
 */
export function edgeStyleToSvg(style, opts = {}) {
    const color = style?.color ?? opts.defaultColor ?? DEFAULT_TRL_EDGE_STYLE.color;
    const width = style?.width ?? opts.defaultWidth ?? DEFAULT_TRL_EDGE_STYLE.width;
    const pattern = style?.pattern ?? DEFAULT_TRL_EDGE_STYLE.pattern;
    const rounded = style?.rounded ?? DEFAULT_TRL_EDGE_STYLE.rounded;
    let strokeDasharray;
    if (pattern === 'dotted')
        strokeDasharray = `${width} ${width * 2}`;
    else if (pattern === 'dashed')
        strokeDasharray = `${width * 3} ${width * 2}`;
    return {
        stroke: color,
        strokeWidth: width,
        strokeDasharray,
        strokeLinecap: rounded ? 'round' : 'butt',
        strokeLinejoin: rounded ? 'round' : 'miter',
    };
}
/** Effective default look for a map backdrop when a field is not authored. */
export const DEFAULT_TRL_MAP_BACKGROUND = {
    image: null,
    color: '#71d9ff',
    opacity: 0.18,
};
/** Sensible defaults for a freshly placed node's dig tuning. */
export const DEFAULT_TRL_NODE_SETTINGS = {
    isCheckpoint: false,
    imagesToClear: 1,
    roundType: 'single',
    basePoints: DEFAULT_TRL_BASE_POINTS,
    pointsDecrement: DEFAULT_TRL_POINTS_DECREMENT,
};
/**
 * Logical galaxy-plane size in design pixels (16:9, "2K"). Node positions are
 * stored NORMALIZED (0..1) against this plane, so it is only a render-time
 * multiplier — changing it uniformly rescales every map with no data migration.
 * The admin builder and the player map view both render against these constants
 * so a planet sits in the same relative spot on every screen.
 */
export const TRL_CANVAS_WIDTH = 2560;
export const TRL_CANVAS_HEIGHT = 1440;
